import { NavLink } from 'react-router-dom';
import { navItems } from '../../config/navItems';
import { useAuth } from '../../hooks/useAuth';
import { isAdminOrAbove } from '../../lib/roles';

// Line icons keyed by route.
const ICONS = {
  '/': (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M3 11l9-8 9 8M5 10v10h5v-6h4v6h5V10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/products': (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3zM4 7.5l8 4.5 8-4.5M12 12v9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/resellers': (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M4 21V5a1 1 0 011-1h9a1 1 0 011 1v16M15 21V9h4a1 1 0 011 1v11M8 8h3M8 12h3M8 16h3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/reports': (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M4 19V5M4 19h16M8 15l3-4 3 2 4-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/users': (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M16 19v-1a4 4 0 00-4-4H7a4 4 0 00-4 4v1M9.5 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM21 19v-1a4 4 0 00-3-3.87M16 4.13A3.5 3.5 0 0116 11" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/alerts': (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M18 9a6 6 0 10-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9zM10.3 19.5a2 2 0 003.4 0" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  '/system-status': (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M3 12h4l2-6 4 12 2-6h6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

// Desktop (>=lg): slim icon rail that expands on hover and can be pinned open.
// Mobile (<lg): off-canvas drawer toggled via `mobileOpen`.
export function Sidebar({ pinned, setPinned, hovered, setHovered, mobileOpen, setMobileOpen }) {
  const { profile, logout } = useAuth();
  const isAdmin = isAdminOrAbove(profile);
  const expanded = pinned || hovered;
  const wide = expanded || mobileOpen;
  const labelCls = `whitespace-nowrap transition-opacity duration-200 opacity-100 ${expanded ? 'lg:opacity-100' : 'lg:opacity-0'}`;
  const name = profile?.fullName || profile?.email || 'Account';
  const initial = (profile?.fullName || profile?.email || '?').charAt(0);

  return (
    <aside
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`fixed left-0 top-0 z-40 h-screen text-white flex flex-col overflow-hidden shadow-2xl
        bg-[linear-gradient(to_bottom,#ffffff_0%,#ffffff_2%,#8b9bf0_16%,#7d8de0_55%,#8f6bc4_100%)]
        dark:bg-[linear-gradient(to_bottom,#0e1626_0%,#0f1830_50%,#0c1220_100%)]
        transition-[transform,width] duration-200
        w-64 ${expanded ? 'lg:w-60' : 'lg:w-[72px]'}
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
    >
      {/* Depth overlays: top light + right edge highlight */}
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(80% 40% at 30% 0%, rgba(255,255,255,0.18) 0%, transparent 60%)' }} />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-white/15" />

      {/* Brand */}
      <div className="relative px-3 py-3">
        {wide ? (
          <div className="flex h-20 items-center justify-center px-2">
            <img src="/cysops-logo.png" alt="CysOps" className="h-16 w-auto" />
          </div>
        ) : (
          <div className="mx-auto h-11 w-11 rounded-xl bg-brand/10 ring-1 ring-brand/20 flex items-center justify-center" title="CysOps">
            <span className="text-brand text-xl font-bold leading-none">C</span>
          </div>
        )}
      </div>

      {/* Section label */}
      <div className={`relative px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/60 ${labelCls}`}>
        Main
      </div>

      {/* Nav */}
      <nav className="relative flex-1 space-y-1 px-3">
        {navItems
          .filter((item) => !item.adminOnly || isAdmin)
          .map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              title={item.label}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                  isActive ? 'bg-white/20 text-white font-medium ring-1 ring-white/25' : 'text-white/85 hover:bg-white/10'
                }`
              }
            >
              <span className="w-6 shrink-0 flex items-center justify-center">{ICONS[item.path]}</span>
              <span className={labelCls}>{item.label}</span>
            </NavLink>
          ))}
      </nav>

      {/* Bottom: pin toggle (desktop) + user card */}
      <div className="relative px-3 pb-3 space-y-1">
        <button
          type="button"
          onClick={() => setPinned((v) => !v)}
          title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
          className="hidden lg:flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-white/80 hover:bg-white/10 transition"
        >
          <span className="w-6 shrink-0 text-center">
            <svg className="mx-auto h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 3h6l-1.5 6 2.5 2H8l2.5-2L9 3zM10 13v4" />
            </svg>
          </span>
          <span className={labelCls}>{pinned ? 'Unpin' : 'Pin open'}</span>
        </button>

        <NavLink
          to="/profile"
          title="View profile"
          onClick={() => setMobileOpen(false)}
          className="mt-1 flex items-center gap-3 rounded-xl bg-white/10 p-2 ring-1 ring-white/15 hover:bg-white/15 transition"
        >
          <span className="h-9 w-9 shrink-0 rounded-full bg-white/25 flex items-center justify-center text-sm font-semibold uppercase">
            {initial}
          </span>
          {wide && (
            <>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-white">{name}</div>
                {profile?.email && <div className="truncate text-xs text-white/60">{profile.email}</div>}
              </div>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); logout(); }}
                title="Sign out"
                aria-label="Sign out"
                className="shrink-0 rounded-lg p-1.5 text-white/70 hover:bg-white/15 hover:text-white transition"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7V4a1 1 0 00-1-1H5a1 1 0 00-1 1v12a1 1 0 001 1h7a1 1 0 001-1v-3M9 10h9m0 0l-3-3m3 3l-3 3" />
                </svg>
              </button>
            </>
          )}
        </NavLink>
      </div>
    </aside>
  );
}
